import { useRef } from "react";
import {
  extractVariableNumbers,
  renumberVariables,
  getNextVariableNumber,
} from "../utils/templateUtils";

/**
 * Custom hook for managing template variables
 */
export const useTemplateVariables = (
  templateData,
  setTemplateData,
  getComponent,
  updateComponent
) => {
  const isUpdatingBodyRef = useRef(false);
  const isUpdatingHeaderRef = useRef(false);

  /**
   * Add a variable at cursor position
   */
  const addVariable = (target = "body", cursorPosition = 0) => {
    const existingVariables = templateData.variables.filter(
      (v) => v.target === target
    );

    // Check limits
    if (target === "header" && existingVariables.length >= 1) {
      return { error: "Only 1 variable allowed in header" };
    }
    if (target === "body" && existingVariables.length >= 10) {
      return { error: "Maximum 10 variables allowed in body" };
    }

    const currentText =
      target === "body"
        ? getComponent("BODY")?.text || ""
        : getComponent("HEADER")?.text || "";

    const nextNumber = getNextVariableNumber(
      currentText,
      target === "header" ? 1 : 10
    );
    const variableText = `{{${nextNumber}}}`;

    // Insert variable at cursor position
    const newText =
      currentText.slice(0, cursorPosition) +
      variableText +
      currentText.slice(cursorPosition);

    // Update text and sync variables
    syncVariablesFromText(newText, target, true);

    return { success: true, newCursorPosition: cursorPosition + variableText.length };
  };

  /**
   * Sync variables from text - extract and renumber variables
   */
  const syncVariablesFromText = (text, target, updateText = true) => {
    const maxVariables = target === "header" ? 1 : 10;
    const { text: updatedText, renumberMap } = renumberVariables(text, maxVariables);

    const variableNumbers = extractVariableNumbers(updatedText);
    const uniqueNumbers = [...new Set(variableNumbers)].slice(0, maxVariables);

    setTemplateData((prev) => {
      const existingVariables = prev.variables.filter((v) => v.target !== target);
      const targetVariables = prev.variables.filter((v) => v.target === target);

      // Create map of existing variables by number
      const existingVarMap = new Map();
      targetVariables.forEach((v) => {
        const varNum = parseInt(v.name);
        if (!isNaN(varNum)) {
          existingVarMap.set(varNum, v);
        }
      });

      // Create new variables array with sequential numbering
      const newVariables = uniqueNumbers.map((oldNum, index) => {
        const newNum = index + 1;
        let existingVar = existingVarMap.get(oldNum);

        // Try to match by position if not found by number
        if (!existingVar && index < targetVariables.length) {
          const varByPosition = targetVariables[index];
          if (varByPosition && parseInt(varByPosition.name) === newNum) {
            existingVar = varByPosition;
          }
        }

        const sampleValue =
          existingVar?.sample !== undefined &&
          existingVar.sample !== null &&
          existingVar.sample !== ""
            ? existingVar.sample
            : "";

        return {
          id: existingVar?.id || Date.now() + index,
          name: newNum.toString(),
          sample: sampleValue,
          target,
        };
      });

      // Update component text if needed
      if (updateText) {
        if (target === "body") {
          updateComponent("BODY", { text: updatedText });
        } else {
          updateComponent("HEADER", { text: updatedText });
        }
      }

      return {
        ...prev,
        variables: [...existingVariables, ...newVariables],
      };
    });

    return updatedText;
  };

  /**
   * Update variable sample value
   */
  const updateVariableSample = (variableId, sampleValue) => {
    setTemplateData((prev) => ({
      ...prev,
      variables: prev.variables.map((v) =>
        v.id === variableId ? { ...v, sample: sampleValue } : v
      ),
    }));
  };

  /**
   * Remove variable from text and variables array
   */
  const removeVariable = (variableId) => {
    const variable = templateData.variables.find((v) => v.id === variableId);
    if (!variable) return;

    const target = variable.target === "body" ? "body" : "header";
    const component = target === "body" ? getComponent("BODY") : getComponent("HEADER");
    const currentText = component?.text || "";

    // Remove the variable from text
    const newText = currentText.replace(
      new RegExp(`\\{\\{${variable.name}\\}\\}`, "g"),
      ""
    );

    // Sync variables (will renumber automatically)
    syncVariablesFromText(newText, target, true);
  };

  /**
   * Handle text change with variable syncing
   */
  const handleTextChange = (newText, target, cursorPosition) => {
    const isUpdatingRef = target === "body" ? isUpdatingBodyRef : isUpdatingHeaderRef;

    if (isUpdatingRef.current) {
      return; // Prevent concurrent updates
    }

    isUpdatingRef.current = true;

    const maxVariables = target === "header" ? 1 : 10;
    const { text: finalText } = renumberVariables(newText, maxVariables);

    const variableNumbers = extractVariableNumbers(finalText);
    const uniqueNumbers = [...new Set(variableNumbers)].slice(0, maxVariables);

    setTemplateData((prev) => {
      // Update component text
      if (target === "body") {
        updateComponent("BODY", { text: finalText });
      } else {
        updateComponent("HEADER", { text: finalText });
      }

      // Update variables
      const existingVariables = prev.variables.filter((v) => v.target !== target);
      const targetVariables = prev.variables.filter((v) => v.target === target);

      const existingVarMap = new Map();
      targetVariables.forEach((v) => {
        const varNum = parseInt(v.name);
        if (!isNaN(varNum)) {
          existingVarMap.set(varNum, v);
        }
      });

      const newVariables = uniqueNumbers.map((oldNum, index) => {
        const newNum = index + 1;
        let existingVar = existingVarMap.get(oldNum);

        if (!existingVar && index < targetVariables.length) {
          const varByPosition = targetVariables[index];
          if (varByPosition && parseInt(varByPosition.name) === newNum) {
            existingVar = varByPosition;
          }
        }

        const sampleValue =
          existingVar?.sample !== undefined &&
          existingVar.sample !== null &&
          existingVar.sample !== ""
            ? existingVar.sample
            : "";

        return {
          id: existingVar?.id || Date.now() + index,
          name: newNum.toString(),
          sample: sampleValue,
          target,
        };
      });

      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);

      return {
        ...prev,
        variables: [...existingVariables, ...newVariables],
      };
    });
  };

  return {
    addVariable,
    updateVariableSample,
    removeVariable,
    syncVariablesFromText,
    handleTextChange,
    isUpdatingBodyRef,
    isUpdatingHeaderRef,
  };
};

